import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@monksflow/monks-ui';
import { differenceInCalendarDays, formatISO, parseISO } from 'date-fns';
import { useFormState, useWatch } from 'react-hook-form';
import { motion } from 'motion/react';
import type { PersonaOption } from '@/entities/persona';
import {
  DESCRIPTION_MAX_LENGTH,
  getMarketLabel,
  isFieldRequired,
  MARKET_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  STATUS_OPTIONS,
  TITLE_MAX_LENGTH,
} from '@/features/create-project/model/schema';
import { PersonaAvatar } from '@/shared/ui/PersonaAvatar';
import { CharCounter, DatePicker, TagInput } from '@/shared/ui';
import { useReviewDetailsStep } from './useReviewDetailsStep';
import { useTranslation } from 'react-i18next';

/**
 * Phase 6 — full v4 Form composition.
 *
 * Replaces the MUI-transitive SelectTextField / TagInputField / DateTextField
 * with raw v4 `<FormField>` + inline Shadcn primitives. Zero MUI in this file
 * or any of its imports.
 *
 * - Selects: inline `<Select>` family (no wrapper component)
 * - Multi-select with chips: `<TagInput<T>>` compound (AD-13)
 * - Date input: `<DatePicker>` compound (AD-12)
 * - All wired through `<FormField>` so error/aria/id plumbing comes free
 */
// Tessra fills Select, Textarea and outline Button with `--background`, but Input with
// `--input` and ComboboxChips with nothing (`--input/30` in dark), which is the card colour
// in dark mode. Every field in the step takes the `--background` fill and the 12px inset of
// the default size, so none of them reads as a hole in the panel.
const FIELD_SURFACE = 'bg-background';
const CHIPS_FIELD_SURFACE = 'bg-background dark:bg-background px-3';
const DATE_TRIGGER_CLASS = 'px-3';
const DATE_PICKER_WINDOW_YEARS = 5;
const today = new Date();
const dateRangeStart = new Date(today.getFullYear() - DATE_PICKER_WINDOW_YEARS, 0, 1);
const dateRangeEnd = new Date(today.getFullYear() + DATE_PICKER_WINDOW_YEARS, 11, 31);

export const ReviewDetailsStep = () => {
  const { t } = useTranslation('projects');

  const { control, tagOptions, personaOptions, getPersonaById, isSubmitting } =
    useReviewDetailsStep();
  const [startDate, deadline] = useWatch({ control, name: ['startDate', 'deadline'] });
  const startDateObj = startDate ? parseISO(startDate) : null;
  const deadlineObj = deadline ? parseISO(deadline) : null;
  const durationDays =
    startDateObj && deadlineObj ? differenceInCalendarDays(deadlineObj, startDateObj) : null;
  const { errors } = useFormState({ control, name: ['startDate', 'deadline'] });
  const dateRowMessageClass = errors.startDate || errors.deadline ? 'min-h-5' : '';

  return (
    <div className="flex flex-col gap-12 w-full max-w-162.5">
      <motion.h2
        className="text-center type-heading-md  text-foreground"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        {t('createProject.reviewDetailsStep.tellUsAboutYourProject')}
      </motion.h2>

      <motion.div
        className="flex flex-col gap-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <FormField
          control={control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="gap-1">
                {t('createProject.reviewDetailsStep.projectType')}
                {isFieldRequired('type') && (
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                )}
              </FormLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('createProject.reviewDetailsStep.selectAProjectType')}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="pointer-events-auto">
                  {PROJECT_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="gap-1">
                {t('createProject.reviewDetailsStep.projectTitle')}
                {isFieldRequired('title') && (
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('createProject.reviewDetailsStep.addProjectTitle')}
                  disabled={isSubmitting}
                  className={FIELD_SURFACE}
                />
              </FormControl>
              <div className="flex items-center justify-between gap-2">
                <FormMessage />
                <CharCounter current={field.value.length} max={TITLE_MAX_LENGTH} />
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="gap-1">
                {t('createProject.reviewDetailsStep.projectDescription')}
                {isFieldRequired('description') && (
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={t('createProject.reviewDetailsStep.addProjectDescription')}
                  rows={3}
                  disabled={isSubmitting}
                />
              </FormControl>
              <div className="flex items-center justify-between gap-2">
                <FormMessage />
                <CharCounter current={field.value.length} max={DESCRIPTION_MAX_LENGTH} />
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="gap-1">{t('createProject.reviewDetailsStep.tags')}</FormLabel>
              <FormControl>
                <TagInput
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={tagOptions}
                  freeSolo
                  disabled={isSubmitting}
                >
                  <TagInput.Chips
                    className={CHIPS_FIELD_SURFACE}
                    placeholder={t('createProject.reviewDetailsStep.typeAndHitEnterTo')}
                  />
                  <TagInput.Content />
                </TagInput>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="markets"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="gap-1">
                {t('createProject.reviewDetailsStep.market')}
                {isFieldRequired('markets') && (
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <TagInput
                  value={field.value}
                  onChange={field.onChange}
                  options={[...MARKET_OPTIONS]}
                  getOptionLabel={getMarketLabel}
                  getOptionValue={(market) => market}
                  disabled={isSubmitting}
                >
                  <TagInput.Chips
                    className={CHIPS_FIELD_SURFACE}
                    placeholder={t('createProject.reviewDetailsStep.selectMarket')}
                  />
                  <TagInput.Content />
                </TagInput>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="gap-1">
                {t('createProject.reviewDetailsStep.projectStatus')}
              </FormLabel>
              <Select
                value={field.value ?? ''}
                onValueChange={field.onChange}
                disabled={isSubmitting}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('createProject.reviewDetailsStep.selectStatus')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="pointer-events-auto">
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-2 gap-2">
            <FormField
              control={control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-1">
                    {t('createProject.reviewDetailsStep.startDate')}
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value ? parseISO(field.value) : null}
                      onChange={(date) => {
                        field.onChange(date ? formatISO(date, { representation: 'date' }) : '');
                      }}
                      disabled={isSubmitting}
                    >
                      <DatePicker.Trigger
                        className={DATE_TRIGGER_CLASS}
                        placeholder={t('createProject.reviewDetailsStep.addDates')}
                      />
                      <DatePicker.Content
                        startMonth={dateRangeStart}
                        endMonth={dateRangeEnd}
                        disabledDates={deadlineObj ? { after: deadlineObj } : undefined}
                      />
                    </DatePicker>
                  </FormControl>
                  <div className={dateRowMessageClass}>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="deadline"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-1">
                    {t('createProject.reviewDetailsStep.deadline')}
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value ? parseISO(field.value) : null}
                      onChange={(date) => {
                        field.onChange(date ? formatISO(date, { representation: 'date' }) : '');
                      }}
                      disabled={isSubmitting}
                    >
                      <DatePicker.Trigger
                        className={DATE_TRIGGER_CLASS}
                        placeholder={t('createProject.reviewDetailsStep.addDates')}
                      />
                      <DatePicker.Content
                        startMonth={dateRangeStart}
                        endMonth={dateRangeEnd}
                        disabledDates={startDateObj ? { before: startDateObj } : undefined}
                      />
                    </DatePicker>
                  </FormControl>
                  <div className={dateRowMessageClass}>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </div>
          {durationDays !== null && durationDays >= 0 && (
            <p className="text-xs text-muted-foreground">
              {t('createProject.reviewDetailsStep.duration', { count: durationDays })}
            </p>
          )}
        </div>

        <FormField
          control={control}
          name="audienceIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="gap-1">
                {t('createProject.reviewDetailsStep.audiences')}
              </FormLabel>
              <FormControl>
                <TagInput<PersonaOption>
                  value={
                    (field.value ?? [])
                      .map((id) => getPersonaById(id))
                      .filter(Boolean) as PersonaOption[]
                  }
                  onChange={(newValue) => field.onChange(newValue.map((v) => v.id))}
                  options={personaOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={isSubmitting}
                >
                  <TagInput.Chips<PersonaOption>
                    className={CHIPS_FIELD_SURFACE}
                    placeholder={t('createProject.reviewDetailsStep.searchForAudiences')}
                    renderChip={(persona, onRemove) => (
                      <TagInput.Chip
                        key={persona.id}
                        variant="default"
                        avatar={
                          <PersonaAvatar
                            name={persona.label}
                            photoAssetId={persona.photoAssetId}
                            size={16}
                            circular
                          />
                        }
                        label={persona.label}
                        removeLabel={t('createProject.reviewDetailsStep.removePersona', {
                          label: persona.label,
                        })}
                        onRemove={onRemove}
                      />
                    )}
                  />
                  <TagInput.Content<PersonaOption>
                    renderOption={(option) => (
                      <div className="flex items-center gap-3">
                        <PersonaAvatar
                          name={option.label}
                          photoAssetId={option.photoAssetId}
                          size={32}
                          circular
                        />
                        <span className="text-sm text-foreground">{option.label}</span>
                      </div>
                    )}
                  />
                </TagInput>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </motion.div>
    </div>
  );
};
